import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Trash2 } from 'lucide-react';
import type { DocumentLibrarySettings as DocumentLibrarySettingsType } from './MenuSettingsTypes';

interface DocumentLibrarySettingsProps {
  settings: DocumentLibrarySettingsType;
  onChange: (settings: DocumentLibrarySettingsType) => void;
  // DED-605: Shared business units from GeneralSection (customerOrg.teams.businessUnitNames)
  sharedBusinessUnits?: string[];
}

type DocumentLibraryField = keyof DocumentLibrarySettingsType;

export const DocumentLibrarySettings: React.FC<DocumentLibrarySettingsProps> = ({
  settings,
  onChange,
  sharedBusinessUnits = [],
}) => {
  const [newItem, setNewItem] = useState({ type: '' as DocumentLibraryField | '', value: '' });

  const handleAddItem = (field: DocumentLibraryField) => {
    if (!newItem.value.trim()) return;

    const updatedSettings = {
      ...settings,
      [field]: [...settings[field], newItem.value.trim()]
    };

    onChange(updatedSettings);
    setNewItem({ ...newItem, value: '' });
  };

  const handleRemoveItem = (field: DocumentLibraryField, index: number) => {
    const updatedList = [...settings[field]];
    updatedList.splice(index, 1);

    onChange({
      ...settings,
      [field]: updatedList
    });
  };

  const handleItemChange = (field: DocumentLibraryField, index: number, value: string) => {
    const updatedList = [...settings[field]];
    updatedList[index] = value;

    onChange({
      ...settings,
      [field]: updatedList
    });
  };

  const protectedDocumentTypes = new Set<string>([
    'Audit',
    'Finding',
    'Corrective Action',
    'Policy',
    'Evidence',
    'Task Evidence',
  ]);

  const renderListEditor = (
    title: string,
    field: keyof DocumentLibrarySettingsType,
    placeholder: string
  ) => (
    <>
      <div className="outline outline-1 outline-border rounded-md p-4">
        <h3 className="text-md font-semibold text-foreground mb-2">{title}</h3>
        <div className="flex gap-2">
          <Input
            placeholder={placeholder}
            value={newItem.type === field ? newItem.value : ''}
            onChange={(e) => setNewItem({ type: field, value: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && handleAddItem(field)}
            className="mb-2"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => handleAddItem(field)}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="border rounded-md divide-y">
          {settings[field].map((item, index) => {
            const isProtected = field === 'documentTypes' && protectedDocumentTypes.has(item);
            return (
              <div key={index} className="flex items-center justify-between p-2">
                <Input
                  value={item}
                  onChange={(e) => !isProtected && handleItemChange(field, index, e.target.value)}
                  className="border-0 shadow-none focus-visible:ring-0"
                  readOnly={isProtected}
                />
                {!isProtected && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleRemoveItem(field, index)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );

  // DED-605: Render read-only business units from GeneralSection
  const renderSharedBusinessUnits = () => (
    <div className="outline outline-1 outline-border rounded-md p-4">
      <h3 className="text-md font-semibold text-foreground mb-2">Business Units</h3>
      <p className="text-sm text-muted-foreground mb-3">
        Business units are managed in General Settings → Business Details.
      </p>
      <div className="border rounded-md divide-y">
        {sharedBusinessUnits.length > 0 ? (
          sharedBusinessUnits.map((unit, index) => (
            <div key={index} className="flex items-center justify-between p-2">
              <span className="text-sm">{unit}</span>
            </div>
          ))
        ) : (
          <div className="p-2 text-sm text-muted-foreground">
            No business units configured. Add them in General Settings.
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {renderListEditor('Document Types', 'documentTypes', 'Add new document type')}
      {renderListEditor('Classifications', 'classifications', 'Add new classification')}
      {renderSharedBusinessUnits()}
    </div>
  );
};
